import React from 'react';
import { useForm, Controller } from 'react-hook-form';
import {
  Button,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  FormControl,
  Stack,
  IconButton,
  InputLabel,
  MenuItem,
  Select,
  TextField,
  Tooltip,
  Typography,
} from '@mui/material';
import { Close } from '@mui/icons-material';
import { LoadingButton } from '@mui/lab';


const CHARACTER_ROLES: Stories.CharacterRole[] = [
  'Mentor',
  'Ally',
  'Foil',
  'Mirror',
  'Obstacle',
  'Symbolic Character',
];

const ROLE_DESCRIPTIONS: Record<Stories.CharacterRole, string> = {
  'Mentor': 'Provides wisdom or guidance',
  'Ally': 'Supports protagonist directly',
  'Foil': 'Contrasts protagonist',
  'Mirror': 'Reflects protagonist\'s struggle',
  'Obstacle': 'Creates barriers',
  'Symbolic Character': 'Represents values, ideas, or themes',
};


type Props = {
  open: boolean;
  onClose: () => void;
  onSubmit: (character: Stories.SupportCharacter) => void;
  character?: Stories.SupportCharacter;
  options?: any;
};

type FormData = {
  name: string;
  description: string;
  role: Stories.CharacterRole;
};

export const SupportCharacterDialog: React.FC<Props> = ({
  open,
  onClose,
  onSubmit,
  character,
  options,
}) => {
  const isEditMode = !!character;

  const { handleSubmit, control, formState: { isSubmitting }, reset } = useForm<FormData>({
    defaultValues: {
      name: character?.name || '',
      description: character?.description || '',
      role: character?.role || 'Mentor',
    },
  });

  React.useEffect(() => {
    if (open) {
      reset({
        name: character?.name || '',
        description: character?.description || '',
        role: character?.role || 'Mentor',
      });
    }
  }, [open, character, reset]);

  const handleFormSubmit = async (data: FormData) => {
    onSubmit({
      name: data.name,
      description: data.description,
      role: data.role,
    });
    onClose();
  };

  return (
    <Dialog fullWidth maxWidth="md" open={open} onClose={onClose} slotProps={{
      paper:
          { variant: 'elevation' },
    }}>
      <form onSubmit={handleSubmit(handleFormSubmit)}>
        <DialogTitle typography="h2">
          {isEditMode ? 'Edit Supporting Character' : 'Add Supporting Character'}
          <IconButton
            aria-label="close"
            onClick={onClose}
            sx={{
              position: 'absolute',
              right: 8,
              top: 8,
            }}
          >
            <Close/>
          </IconButton>
        </DialogTitle>
        <DialogContent>
          <Stack spacing={2} mt={1}>
            <Stack direction={{ xs: 'column', md: 'row' }} spacing={2}>
              <Stack flex={1}>
                <Controller
                  name="name"
                  control={control}
                  rules={{ required: 'Name is required' }}
                  render={({ field, fieldState }) => (
                    <TextField
                      {...field}
                      label="Name"
                      variant="outlined"
                      fullWidth
                      error={!!fieldState.error}
                      helperText={fieldState.error?.message}
                      placeholder="e.g., Obi-Wan Kenobi"
                    />
                  )}
                />
              </Stack>
              <Stack flex={1}>
                <Controller
                  name="role"
                  control={control}
                  rules={{ required: 'Role is required' }}
                  render={({ field, fieldState }) => (
                    <FormControl fullWidth error={!!fieldState.error}>
                      <InputLabel id="role-label">Role</InputLabel>
                      <Select
                        {...field}
                        labelId="role-label"
                        label="Role"
                        displayEmpty
                        renderValue={(selected) => {
                          if (!selected) {
                            return <span style={{ color: '#a2a2a2' }}>Select character role</span>;
                          }
                          return <span>{selected}</span>;
                        }}
                      >
                        <MenuItem value="">
                          <span style={{ color: '#a2a2a2' }}>Select character role</span>
                        </MenuItem>
                        {CHARACTER_ROLES.map((role) => (
                          <MenuItem key={role} value={role}>
                            <Tooltip title={ROLE_DESCRIPTIONS[role]} placement="right">
                              <span>{role}</span>
                            </Tooltip>
                          </MenuItem>
                        ))}
                      </Select>
                      {fieldState.error && (
                        <Typography variant="caption" color="error">
                          {fieldState.error.message}
                        </Typography>
                      )}
                    </FormControl>
                  )}
                />
              </Stack>
            </Stack>
            <Stack width="100%">
              <Controller
                name="description"
                control={control}
                render={({ field, fieldState }) => (
                  <TextField
                    {...field}
                    label="Description"
                    variant="outlined"
                    fullWidth
                    multiline
                    minRows={4}
                    error={!!fieldState.error}
                    helperText={fieldState.error?.message}
                    placeholder="Describe the character's significance in the story"
                    sx={{ width: '100%' }}
                  />
                )}
              />
            </Stack>
          </Stack>
        </DialogContent>
        <DialogActions sx={{ justifyContent: 'space-between', p: 2 }}>
          <Button type="button" variant="text" onClick={onClose}>Cancel</Button>
          <LoadingButton
            type="submit"
            loading={isSubmitting}
            variant="contained"
          >
            {isEditMode ? 'Update' : 'Add'}
          </LoadingButton>
        </DialogActions>
      </form>
    </Dialog>
  );
};
